import { useCallback, useEffect, useMemo, useState } from 'react'

import { DataLakeEnums } from 'dashboard-services/api/dataLake'
import { ReduxService } from 'dashboard-services/services'

import { groupsDatalakeActions } from 'actions/modules/datalake'

import { updateForwardCurveMetadataField } from '../../forwardCurveFieldsService'
import groupDialogService from '../groupDialogService'

export default (() => {
  const EMPTY_ARRAY = []

  const normalizeFields = (fields = []) => fields.map(field => field.type === "INTEGER" ? ReduxService.updateObject(field, { type: DataLakeEnums.GROUP_FIELD_TYPE.NUMBER }) : field)

  const getInitialDetails = initialGroup => {
    const fields = normalizeFields(initialGroup?.fields || EMPTY_ARRAY)
    return {
      description: initialGroup?.description || "",
      groupTags: initialGroup?.groupTags || EMPTY_ARRAY,
      fields,
      isDataTimeFrameEnabled: groupDialogService.hasDataTimeFrame({ fields }),
      shouldBeForwardCurvesGroup: groupDialogService.isForwardCurvesGroup({ fields })
    }
  }

  const withoutError = ({ errors = [], field }) => errors.filter(error => error.field !== field)

  const useGroupDialogDetails = ({ initialGroup }) => {
    const [isInitialized, setIsInitialized] = useState(!!initialGroup),
          [initialDetails, setInitialDetails] = useState(() => getInitialDetails(initialGroup)),
          [groupName, setGroupName] = useState(initialGroup?.groupName || ""),
          [description, setDescription] = useState(initialDetails.description),
          [groupTags, setGroupTags] = useState(initialDetails.groupTags),
          [fields, setFields] = useState(initialDetails.fields),
          [isDataTimeFrameEnabled, setIsDataTimeFrameEnabled] = useState(initialDetails.isDataTimeFrameEnabled),
          [shouldBeForwardCurvesGroup, setShouldBeForwardCurvesGroup] = useState(initialDetails.shouldBeForwardCurvesGroup),
          [errors, setErrors] = useState(EMPTY_ARRAY),
          fieldsToDisplay = useMemo(() => groupDialogService.getFieldsToDisplay({ fields }), [fields]),

          onChangeGroupName = useCallback(value => {
            setGroupName(value)
            setErrors(errors => withoutError({ errors, field: "name" }))
          }, []),
          onChangeDescription = useCallback(value => {
            setDescription(value)
            setErrors(errors => withoutError({ errors, field: "description" }))
          }, []),
          onChangeGroupTags = useCallback((_, { data }) => groupsDatalakeActions.onChangeGroupTags({ data: data || EMPTY_ARRAY, onChangeAction: setGroupTags }), []),
          onAddField = useCallback(() => setFields(fields => fields.concat(groupsDatalakeActions.EMPTY_FIELD)), []),
          onChangeField = useCallback((value, { name, index }) => setFields(fields => {
            const fullIndex = groupDialogService.getFullFieldIndex({ fields, displayIndex: index })
            return fields.map((field, fieldIndex) => fieldIndex === fullIndex ? ReduxService.updateObject(field, { [name]: value }) : field)
          }), []),
          onRemoveField = useCallback(index => setFields(fields => {
            const fullIndex = groupDialogService.getFullFieldIndex({ fields, displayIndex: index })
            return fields.filter((_, fieldIndex) => fieldIndex !== fullIndex)
          }), []),
          onChangeForwardCurves = useCallback(() => {
            const nextValue = !shouldBeForwardCurvesGroup
            setShouldBeForwardCurvesGroup(nextValue)
            setFields(fields => updateForwardCurveMetadataField({ fields, shouldBeForwardCurvesGroup: nextValue }))
          }, [shouldBeForwardCurvesGroup])

    useEffect(() => {
      if(!isInitialized && initialGroup) {
        const loadedDetails = getInitialDetails(initialGroup)
        setInitialDetails(loadedDetails)
        setGroupName(initialGroup.groupName || "")
        setDescription(loadedDetails.description)
        setGroupTags(loadedDetails.groupTags)
        setFields(loadedDetails.fields)
        setIsDataTimeFrameEnabled(loadedDetails.isDataTimeFrameEnabled)
        setShouldBeForwardCurvesGroup(loadedDetails.shouldBeForwardCurvesGroup)
        setIsInitialized(true)
      }
    }, [initialGroup, isInitialized])

    return useMemo(() => ({
      isInitialized,
      initialDetails,
      groupName,
      description,
      groupTags,
      fields,
      fieldsToDisplay,
      isDataTimeFrameEnabled,
      shouldBeForwardCurvesGroup,
      errors,
      setErrors,
      onChangeGroupName,
      onChangeDescription,
      onChangeGroupTags,
      onAddField,
      onChangeField,
      onRemoveField,
      onChangeDataTimeFrame: setIsDataTimeFrameEnabled,
      onChangeForwardCurves
    }), [description, errors, fields, fieldsToDisplay, groupName, groupTags, initialDetails, isDataTimeFrameEnabled, isInitialized, onAddField, onChangeDescription, onChangeField, onChangeForwardCurves, onChangeGroupName, onChangeGroupTags, onRemoveField, shouldBeForwardCurvesGroup])
  }

  return {
    useGroupDialogDetails
  }
})()
